import { Check, ChevronLeft, ChevronRight } from "@tamagui/lucide-icons";
import type React from "react";
import {
  type CalendarProps,
  type DateData,
  Calendar as ReactNativeCalendar,
} from "react-native-calendars";
import type { DayProps } from "react-native-calendars/src/calendar/day";
import {
  Circle,
  type GetThemeValueForKey,
  getTokens,
  getVariableValue,
  View,
} from "tamagui";
import { isSameDay } from "../core/dayJs";
import { StartOfWeek } from "../plans/types";
import { Body } from "./Body";
import { Subtext } from "./Subtext";

export const CalendarDay: React.FC<
  DayProps & {
    date?: DateData;
    backgroundColor?: string;
    color?: string;
    userSelects?: string[];
  }
> = ({ backgroundColor, color, date, userSelects, ...props }) => {
  if (!date) {
    return null;
  }

  const isToday = isSameDay(new Date(date.timestamp), new Date());

  return (
    <View
      key={date.dateString}
      height={"$size.large"}
      width={"100%"}
      opacity={props.state === "disabled" ? 0.3 : 1}
      alignItems="center"
      justifyContent="center"
      position="relative"
      cursor="pointer"
      onPress={() => props.onPress?.(date)}
    >
      {isToday && (
        <View
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          alignItems="center"
          justifyContent="center"
          zIndex={0}
        >
          <Circle
            backgroundColor={"#e9f9ee"}
            width={"$size.medium"}
            height={"$size.medium"}
          />
        </View>
      )}

      <View
        height={"$size.large"}
        width={"100%"}
        style={{ backgroundColor: backgroundColor || props.marking?.color }}
        alignItems="center"
        justifyContent="center"
      >
        {userSelects?.length ? (
          <View position="absolute" top={2} right={5}>
            <Subtext style={{ color }} opacity={0.5}>
              {userSelects.length}
            </Subtext>
          </View>
        ) : undefined}
        <Body style={{ color }} muted={props.state === "disabled"}>
          {date.day}
        </Body>
        {props.marking?.selected ? (
          <View position="absolute" bottom={3} right={5}>
            <Check
              color={color as GetThemeValueForKey<"color">}
              size={"$size.small"}
            />
          </View>
        ) : undefined}
      </View>
    </View>
  );
};

export const Calendar: React.FC<
  CalendarProps & { startOfWeek: StartOfWeek }
> = (props) => {
  const sizeSmaller = getVariableValue(getTokens().size.smaller);

  return (
    <ReactNativeCalendar
      firstDay={props.startOfWeek === StartOfWeek.Monday ? 1 : 0}
      dayComponent={CalendarDay}
      {...props}
      theme={{
        textDayFontFamily: "Inter",
        textMonthFontFamily: "Inter",
        textDayHeaderFontFamily: "Inter",
        weekVerticalMargin: 0,
        textDayHeaderFontSize: sizeSmaller,
        textDayHeaderFontWeight: "300",
      }}
      renderArrow={(direction) =>
        direction === "left" ? <ChevronLeft /> : <ChevronRight />
      }
    />
  );
};
